import { Button, Flex, Icon } from '@chakra-ui/react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { FC } from 'react';

interface ChecklistNavFooterProps {
  /** True when there is an earlier Needs-Review item to go to. */
  hasPrev: boolean;
  /** True when there is a later Needs-Review item to go to. */
  hasNext: boolean;
  /**
   * Noun for the unit being cycled, e.g. "checklist item" (SkySlope) or "item"
   * (non-SkySlope, where the rail is folder-grouped single documents). Used in
   * the "Previous {noun}" / "Next {noun}" labels.
   */
  itemNoun: string;
  onPrev: () => void;
  onNext: () => void;
}

/**
 * Pinned bottom bar to cycle through the Needs-Review items. Shown only while a
 * Needs-Review item is open (the parent hides it for "Other documents"). Each
 * button is disabled at the respective end of the list.
 */
export const ChecklistNavFooter: FC<ChecklistNavFooterProps> = ({
  hasPrev,
  hasNext,
  itemNoun,
  onPrev,
  onNext,
}) => (
  <Flex
    align="center"
    bg="compliance.group.expanded.bg"
    borderTopColor="compliance.border.primary"
    borderTopWidth={1}
    boxShadow="0px 0px 24px 0px rgba(0, 0, 0, 0.55)"
    flexShrink={0}
    justify="space-between"
    px={6}
    py={4}
    w="full"
  >
    <Button
      _hover={{ bg: 'whiteAlpha.100' }}
      borderColor="whiteAlpha.300"
      borderRadius="8px"
      borderWidth={1}
      color="compliance.text.primary"
      fontSize="sm"
      fontWeight="medium"
      isDisabled={!hasPrev}
      leftIcon={<Icon as={ArrowLeftIcon} boxSize="14px" />}
      size="sm"
      variant="outline"
      onClick={onPrev}
    >
      Previous {itemNoun}
    </Button>

    <Button
      _hover={{ bg: 'whiteAlpha.100' }}
      borderColor="cyan.500"
      borderRadius="8px"
      borderWidth={1}
      color="cyan.500"
      fontSize="sm"
      fontWeight="medium"
      isDisabled={!hasNext}
      rightIcon={<Icon as={ArrowRightIcon} boxSize="14px" />}
      size="sm"
      variant="outline"
      onClick={onNext}
    >
      Next {itemNoun}
    </Button>
  </Flex>
);
